import { Box, Typography, useTheme, ButtonBase } from '@mui/material';
import { useState, useMemo, JSX } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useArgumentsMetadataQuery } from '@tymely/services/hooks';
import { Divider } from '@mui/material';

import ToolResponseIcon from './assets/tool-response-icon.svg';
import { TICKET_OPERATIONS, EVENT_KINDS, EventKind, EVENT_LABELS, POLICY_TYPES } from '../../../constants';
import { formatBubbleDate } from '../utils';
import { PreFilterEvent } from './components/PreFilterEvent';
import { EventItemProps } from './types';
import { usePolicyNode } from './hooks/usePolicyNode';
import CommentIdWithTooltip from './components/CommentIdWithTooltip';
import IntentSuggestionHeader from './components/IntentSuggestionHeader';
import ToolResponseHeader from './components/ToolResponseHeader';
import FollowUpHeader from './components/FollowUpHeader';
import PreFilterHeader from './components/PreFilterHeader';
import SystemPromptHeader from './components/SystemPromptHeader';
import { eventOperations } from '../constants';
import { SystemPromptEvent } from './components/SystemPromptEvent';
import { IntentSuggestionEvent } from './components/IntentSuggestionEvent';
import { ToolResponseEvent } from './components/ToolResponseEvent';
import { FollowUpEvent } from './components/FollowUpEvent';
import ThinkingPart from './components/ThinkingPart';
import AlertEventHeader from './components/AlertEventHeader';
import ConverserValidatorEvent from './components/ConverserValidatorEvent';
import ToolCallHeader from './ToolCallHeader';

const operationToEventKindMap: Record<string, EventKind> = {
    [eventOperations.CONVERSER_VALIDATOR]: EVENT_KINDS.CONVERSER_VALIDATOR,
};

const ChatEvent = ({ event, toolsPerComment, globalSystemPrompt, clientSystemPrompt }: EventItemProps) => {
    const theme = useTheme();
    const argumentsMetadata = useArgumentsMetadataQuery();

    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [selectedPromptId, setSelectedPromptId] = useState<number>();
    const [showThinking, setShowThinking] = useState(false);

    const eventData = event.additional_data;

    let kind: EventKind;
    if (event.operation === TICKET_OPERATIONS.END_EVALUATING_POLICY) {
        const policyType = eventData?.decision?.policy_type;
        kind = policyType === POLICY_TYPES.PRE_FILTER ? EVENT_KINDS.PRE_FILTER : EVENT_KINDS.FOLLOW_UP;
    } else {
        kind = operationToEventKindMap[event.operation] ?? (eventData?.audit_log?.kind as EventKind);
    }
    const createdAt = event.created_date;
    const { data: nodeData } = usePolicyNode({
        organizationId: event.organization_id,
        policySetId: eventData?.audit_log?.policy_set_id,
        nodeId: eventData?.audit_log?.node_id,
    });

    const argumentMetadataTitle = useMemo(() => {
        if (!nodeData?.arg_metadata_id || !argumentsMetadata.data) return null;
        return argumentsMetadata.data.find((am) => am.id === nodeData.arg_metadata_id)?.title;
    }, [nodeData?.arg_metadata_id, argumentsMetadata.data]);

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const handlePromptClick = () => {
        const { prompt_id } = eventData.audit_log;
        if (selectedPromptId === prompt_id) {
            toggleSection('prompt');
        } else {
            setSelectedPromptId(prompt_id);
            setExpandedSections((prev) => ({ ...prev, prompt: true }));
        }
    };

    const eventIcons: Record<EventKind, JSX.Element> = {
        [EVENT_KINDS.SYSTEM_PROMPT]: <InfoIcon sx={{ fontSize: 20, color: theme.palette.info.dark }} />,
        [EVENT_KINDS.INTENT_SUGGESTION]: <LightbulbIcon sx={{ fontSize: 20, color: theme.palette.warning.dark }} />,
        [EVENT_KINDS.TOOL_RESPONSE]: (
            <img src={ToolResponseIcon} alt="Tool Response" style={{ width: 20, height: 20 }} />
        ),
        [EVENT_KINDS.PRE_FILTER]: <FilterAltIcon sx={{ fontSize: 20, color: theme.palette.error.dark }} />,
        [EVENT_KINDS.CONVERSER_VALIDATOR]: <SmartToyIcon sx={{ fontSize: 20, color: theme.palette.success.dark }} />,
        [EVENT_KINDS.TOOL_REQUEST]: <PlayArrowIcon sx={{ fontSize: 20, color: theme.palette.error.dark }} />,
        [EVENT_KINDS.ESCALATION]: <ReportProblemIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />,
        [EVENT_KINDS.ERROR]: <ReportProblemIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />,
        [EVENT_KINDS.FOLLOW_UP]: (
            <Box component="span" pb={2} mb={2} sx={{ fontSize: 20 }}>
                ⏰
            </Box>
        ),
    };

    if (!Object.values(EVENT_KINDS).includes(kind)) {
        return null;
    }

    const renderEventDetails = () => {
        switch (kind) {
            case EVENT_KINDS.SYSTEM_PROMPT:
                return (
                    <SystemPromptEvent
                        eventData={eventData}
                        knowledgeBaseSystemPrompt={eventData.audit_log.rendered_template}
                        globalSystemPrompt={globalSystemPrompt}
                        clientSystemPrompt={clientSystemPrompt}
                        isExpanded={expandedSections.prompt}
                        onToggle={handlePromptClick}
                    />
                );

            case EVENT_KINDS.INTENT_SUGGESTION:
                return (
                    <IntentSuggestionEvent
                        eventData={eventData}
                        isExpanded={expandedSections.intent_suggestion}
                        tools={toolsPerComment}
                    />
                );

            case EVENT_KINDS.FOLLOW_UP:
                return (
                    <FollowUpEvent
                        eventData={eventData}
                        workflowId={nodeData?.workflow?.id}
                        isExpanded={expandedSections.result}
                    />
                );

            case EVENT_KINDS.TOOL_RESPONSE:
                return (
                    <ToolResponseEvent
                        eventData={{ audit_log: eventData.audit_log }}
                        argumentsMetadata={argumentsMetadata}
                        workflowId={nodeData?.workflow?.id}
                        title={
                            argumentMetadataTitle || eventData.audit_log.decision_title || eventData.audit_log.node_id
                        }
                        isExpanded={expandedSections.result}
                    />
                );

            case EVENT_KINDS.PRE_FILTER:
                return <PreFilterEvent eventData={eventData} isExpanded={expandedSections.conditions} />;

            case EVENT_KINDS.TOOL_REQUEST: {
                const thinkingPart = eventData?.audit_log?.thinking;
                if (thinkingPart && showThinking) {
                    return <ThinkingPart content={thinkingPart.content} />;
                }
                return null;
            }
            case EVENT_KINDS.ESCALATION:
            case EVENT_KINDS.ERROR: {
                const isEscalation = kind === EVENT_KINDS.ESCALATION;
                const content = isEscalation
                    ? eventData?.audit_log?.escalation_reason || 'No reason provided'
                    : eventData?.audit_log?.content || 'No details provided';
                const isExpanded = !!expandedSections[kind];
                if (!isExpanded) return null;
                return (
                    <Box
                        sx={{
                            backgroundColor: isEscalation ? '#fff3e0' : '#e3f2fd',
                            p: 2,
                            borderRadius: 2,
                            mb: 2,
                            mt: 1,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {content}
                        </Typography>
                    </Box>
                );
            }
            case EVENT_KINDS.CONVERSER_VALIDATOR: {
                const { responseMessage, triggerMessage } = eventData;
                const isExpanded = !!expandedSections.converser_validator;
                return (
                    <ConverserValidatorEvent
                        responseMessage={responseMessage}
                        triggerMessage={triggerMessage}
                        isExpanded={isExpanded}
                    />
                );
            }
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyItems: 'center', mt: 0.5, mb: 0.5 }}>
                <Box sx={{ mr: 2, pt: 0.5 }}>{eventIcons[kind as EventKind]}</Box>
                <Box sx={{ flex: 1 }}>
                    {kind === EVENT_KINDS.INTENT_SUGGESTION && (
                        <IntentSuggestionHeader
                            kind={kind}
                            eventData={eventData}
                            expanded={!!expandedSections.intent_suggestion}
                            onToggle={() => toggleSection('intent_suggestion')}
                        />
                    )}
                    {kind === EVENT_KINDS.TOOL_RESPONSE && (
                        <ToolResponseHeader
                            kind={kind}
                            eventData={eventData}
                            expanded={!!expandedSections.result}
                            onToggle={() => toggleSection('result')}
                        />
                    )}
                    {kind === EVENT_KINDS.FOLLOW_UP && eventData.decision && (
                        <FollowUpHeader
                            eventData={eventData}
                            expanded={!!expandedSections.result}
                            onToggle={() => toggleSection('result')}
                        />
                    )}
                    {kind === EVENT_KINDS.PRE_FILTER && eventData.decision && (
                        <PreFilterHeader
                            kind={kind}
                            commentId={event.comment_id}
                            eventData={eventData}
                            expanded={!!expandedSections.conditions}
                            onToggle={() => toggleSection('conditions')}
                        />
                    )}
                    {kind === EVENT_KINDS.SYSTEM_PROMPT && (
                        <SystemPromptHeader
                            eventData={eventData}
                            expanded={!!expandedSections.prompt}
                            onToggle={handlePromptClick}
                        />
                    )}
                    {[EVENT_KINDS.ESCALATION, EVENT_KINDS.ERROR].includes(kind) && (
                        <AlertEventHeader
                            label={EVENT_LABELS[kind]}
                            isExpanded={!!expandedSections[kind]}
                            onToggle={() => toggleSection(kind)}
                            commentId={eventData?.audit_log?.comment_id}
                        />
                    )}
                    {kind === EVENT_KINDS.CONVERSER_VALIDATOR && (
                        <ButtonBase
                            onClick={() => toggleSection('converser_validator')}
                            sx={{ width: '100%', textAlign: 'left', display: 'block', p: 0, borderRadius: 1 }}
                            disableRipple
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'inline', mr: 1 }}>
                                    {EVENT_LABELS[kind]}
                                </Typography>
                                {eventData?.audit_log?.comment_id && (
                                    <CommentIdWithTooltip commentId={eventData.audit_log.comment_id} />
                                )}
                                {expandedSections.converser_validator ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                        </ButtonBase>
                    )}
                    {kind === EVENT_KINDS.TOOL_REQUEST && (
                        <ToolCallHeader
                            eventData={eventData}
                            showThinking={showThinking}
                            setShowThinking={setShowThinking}
                            organizationId={event.organization_id}
                        />
                    )}
                </Box>
                <Box sx={{ ml: 2, minWidth: 120, textAlign: 'right' }}>
                    <Typography variant="caption" color="GrayText">
                        {kind === EVENT_KINDS.CONVERSER_VALIDATOR || kind === EVENT_KINDS.SYSTEM_PROMPT
                            ? formatBubbleDate(eventData?.message?.timestamp || `${createdAt}Z`, false, true)
                            : formatBubbleDate(`${createdAt}Z`, false, true)}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ pl: 4.5 }}>{renderEventDetails()}</Box>
            <Divider sx={{ borderBottomWidth: 2 }} />
        </Box>
    );
};

export default ChatEvent;
